import {
  BORDER_CONFIG,
  menuStyle,
  styleOverridesAutocomplete,
  styleOverridesSelect
} from '@sm-dashboard/form-provider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { ThemeOptions, Theme } from '@mui/material';

export const merginSelectPaperStyle = {
  '&:has(ul)': {
    '&:has(li)': {
      '&:not(:has(li:nth-of-type(6)))': {
        marginTop: -5.5
      }
    }
  }
};

export const commonComponentsStyles: ThemeOptions['components'] = {
  MuiTextField: {
    variants: [
      {
        props: { multiline: true },
        style: {
          display: 'contents',
          // Change border radius for inner input base in multiline variant
          '& .MuiInputBase-root': {
            borderRadius: 25
          }
        }
      }
    ]
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: '37px',
        boxShadow: 'none',
        '&.MuiButton-sizeSmall': {
          height: '31px'
        }
      },
      contained: {
        color: 'white'
      }
    }
  },
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: <CheckBoxOutlinedIcon />
    },
    styleOverrides: {
      root: {
        color: 'black',
        '&.Mui-checked:not(.Mui-disabled)': {
          color: 'black'
        }
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme, ownerState }: { theme: Theme; ownerState?: { multiline?: boolean } }) => ({
        borderRadius: ownerState?.multiline ? '12px' : '50px', // Use smaller radius for multiline
        lineHeight: '166%',
        ...(ownerState?.multiline && {
          overflow: 'auto'
        }),
        '& input': {
          paddingTop: '7px',
          paddingBottom: '7px'
        },
        backgroundColor: theme.palette.background.default,
        '&.MuiInputBase-sizeSmall': {
          fontSize: '0.875rem',
          '& input': {
            paddingTop: '5.5px',
            paddingBottom: '5.5px'
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: BORDER_CONFIG
      },
      root: ({ ownerState }: { ownerState?: { multiline?: boolean } }) => ({
        borderRadius: ownerState?.multiline ? '12px' : '50px', // Use smaller radius for multiline
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: BORDER_CONFIG
        }
      })
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      shrink: {
        color: '#000000 !important',
        marginLeft: 'calc(3% - 0rem)',
        '&.Mui-disabled': {
          color: '#00000038 !important'
        },
        '& + .MuiOutlinedInput-root fieldset > legend': {
          paddingLeft: '3%',
          marginLeft: '3%'
        }
      },
      root: {
        '&:not(.MuiFormLabel-filled):not(.Mui-focused)': {
          top: '-10px',
          '&.Mui-disabled': {
            color: '#00000038'
          },
          '&.MuiInputLabel-sizeSmall': {
            top: '-10%',
            fontSize: '0.875rem'
          }
        }
      }
    }
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: <KeyboardArrowDownIcon />,
      slotProps: {
        paper: {
          sx: {
            '& .MuiMenu-paper': merginSelectPaperStyle
          }
        }
      }
    },
    styleOverrides: {
      inputRoot: {
        height: '37px',
        boxSizing: 'border-box',
        paddingRight: '9px !important',
        '&.MuiInputBase-sizeSmall': {
          height: '31px'
        }
      },
      input: styleOverridesAutocomplete.input,
      endAdornment: styleOverridesAutocomplete.endAdornment,
      root: styleOverridesAutocomplete.root,
      popper: ({ theme }) => menuStyle(theme)
    }
  },
  MuiInputAdornment: {
    defaultProps: {
      position: 'end'
    }
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: KeyboardArrowDownIcon,
      MenuProps: {
        sx: {
          '& .MuiMenu-paper': merginSelectPaperStyle
        }
      }
    },
    styleOverrides: styleOverridesSelect
  },
  MuiMenu: {
    styleOverrides: {
      root: ({ theme }) => menuStyle(theme)
    }
  },
  MuiSlider: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        '& .MuiSlider-thumb': {
          marginRight: ownerState.size === 'medium' ? '-20px' : '-12px'
        },
        '& .MuiSlider-mark': {
          display: 'none'
        },
        '& .MuiSlider-markLabel': {
          marginRight: '-2ch',
          fontSize: '0.75rem'
        },
        '& .MuiSlider-rail': {
          backgroundColor: theme.palette.background.paper
        }
      })
    },
    defaultProps: {
      size: 'medium',
      valueLabelDisplay: 'auto'
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: BORDER_CONFIG
      }
    }
  },
  MuiChip: {
    defaultProps: {
      size: 'small'
    }
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        borderRadius: '25px',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#555'
          }
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: '#f1f1f1'
        }
      },
      '*': {
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '3px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#a8a8a8'
          }
        }
      }
    }
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        p: { xs: 1.5, md: 2 }
      }
    }
  }
};
